import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, DataTable } from "../components/ui";
import { apiConfig } from "../config/authConfig";

const TIPO_APPARECCHIATURA_OPTIONS = {
  100000000: "Ascensore/Montacarichi",
  100000001: "Caldaia/Centrale termica",
  100000002: "Climatizzazione",
  100000003: "Impianto elettrico",
  100000004: "Impianto antincendio",
  100000005: "Impianto gas medicali",
  100000006: "Gruppo elettrogeno",
  100000007: "Autoclave/Sterilizzatrice",
  100000008: "Apparecchiatura medicale",
  100000009: "Impianto idrico/Sanitario",
  100000010: "Impianto fotovoltaico",
  100000011: "Cancello/Porta automatica",
  100000012: "Sistema di sicurezza",
  100000013: "Cucina industriale",
  100000014: "Lavanderia industriale",
  100000099: "Altro",
};

const STATO_OPTIONS = {
  100000000: { label: "Funzionante", variant: "success", icon: "✅" },
  100000001: { label: "Guasta", variant: "danger", icon: "❌" },
  100000002: { label: "In manutenzione", variant: "warning", icon: "🔧" },
  100000003: { label: "Dismessa", variant: "default", icon: "🚫" },
};

const TIPO_ICONS = {
  100000000: "🛗", // Ascensore
  100000001: "🔥", // Caldaia
  100000002: "❄️", // Clima
  100000003: "⚡", // Elettrico
  100000004: "🧯", // Antincendio
  100000005: "🏥", // Gas medicali
  100000006: "🔌", // Gruppo elettrogeno
  100000007: "🧫", // Autoclave
  100000008: "💉", // Medicale
  100000009: "🚿", // Idrico
  100000010: "☀️", // Fotovoltaico
  100000011: "🚪", // Cancello
  100000012: "🔒", // Sicurezza
  100000013: "🍳", // Cucina
  100000014: "🧺", // Lavanderia
  100000099: "📦", // Altro
};

function ApparecchiatureListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, apiRequest, getAccessToken } = useAuth();
  const { showToast } = useToast();

  const [apparecchiature, setApparecchiature] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [rsaList, setRsaList] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");

  // Filtri da URL
  const rsaIdFilter = searchParams.get("rsaId") || "";
  const tipoFilter = searchParams.get("tipo") || "";
  const statoFilter = searchParams.get("stato") || "";
  const scadenzaDaFilter = searchParams.get("scadenzaDa") || "";
  const scadenzaAFilter = searchParams.get("scadenzaA") || "";

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canCreate = isAdmin || isRspp;

  // Carica RSA solo una volta
  useEffect(() => {
    apiRequest("/rsa")
      .then((rsaData) => setRsaList(rsaData.value || []))
      .catch(() => {});
  }, [apiRequest]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Costruisci query params
        const params = new URLSearchParams();
        if (rsaIdFilter) params.append("rsaId", rsaIdFilter);
        if (tipoFilter) params.append("tipoApparecchiatura", tipoFilter);
        if (statoFilter) params.append("stato", statoFilter);
        if (scadenzaDaFilter) params.append("scadenzaDa", scadenzaDaFilter);
        if (scadenzaAFilter) params.append("scadenzaA", scadenzaAFilter);
        if (searchTerm) params.append("search", searchTerm);
        params.append("soloAttive", "false");
        params.append("top", pageSize);
        params.append("skip", (page - 1) * pageSize);

        // Carica apparecchiature
        const appData = await apiRequest(
          `/apparecchiature?${params.toString()}`,
        );
        setApparecchiature(appData.value || []);
        setTotalCount(appData.count || 0);

        // Carica statistiche (solo alla prima pagina / cambio filtri)
        const statsParams = rsaIdFilter ? `?rsaId=${rsaIdFilter}` : "";
        const statsData = await apiRequest(
          `/apparecchiature/stats/overview${statsParams}`,
        ).catch(() => null);
        setStats(statsData);
      } catch (error) {
        console.error("Error fetching apparecchiature:", error);
        showToast("Errore nel caricamento delle apparecchiature", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    apiRequest,
    rsaIdFilter,
    tipoFilter,
    statoFilter,
    scadenzaDaFilter,
    scadenzaAFilter,
    page,
    pageSize,
    searchTerm,
    showToast,
  ]);

  // Reset pagina quando cambiano i filtri o la ricerca
  const handleFilterChange = (key, value) => {
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleSearch = (term) => {
    setPage(1);
    setSearchTerm(term);
  };

  const handlePageSizeChange = (newSize) => {
    setPage(1);
    setPageSize(parseInt(newSize));
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const token = await getAccessToken();
      const params = new URLSearchParams();
      if (rsaIdFilter) params.append("rsaId", rsaIdFilter);
      if (tipoFilter) params.append("tipoApparecchiatura", tipoFilter);
      if (statoFilter) params.append("stato", statoFilter);
      if (scadenzaDaFilter) params.append("scadenzaDa", scadenzaDaFilter);
      if (scadenzaAFilter) params.append("scadenzaA", scadenzaAFilter);

      const response = await fetch(
        `${apiConfig.baseUrl}/apparecchiature/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) throw new Error(`Errore export: ${response.status}`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `apparecchiature_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      showToast("Errore durante l'esportazione Excel", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      key: "tipo",
      label: "Tipo",
      render: (row) => (
        <span className="flex items-center gap-2">
          <span className="text-xl">
            {TIPO_ICONS[row.tipoApparecchiatura] || "📦"}
          </span>
          <span className="text-sm text-gray-600">
            {row.tipoApparecchiaturaLabel}
          </span>
        </span>
      ),
    },
    {
      key: "nome",
      label: "Nome",
      render: (row) => (
        <div>
          <Link
            to={`/apparecchiature/${row.id}`}
            className="text-primary-600 hover:underline font-medium"
          >
            {row.nome}
          </Link>
          {row.marca && (
            <p className="text-xs text-gray-500">
              {row.marca} {row.modello}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "codici",
      label: "Codici",
      render: (row) => (
        <div className="text-sm">
          {row.matricolaInterna && (
            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs block mb-1">
              INT: {row.matricolaInterna}
            </span>
          )}
          {row.codiceSeriale && (
            <span className="font-mono bg-blue-50 px-1.5 py-0.5 rounded text-xs text-blue-700">
              S/N: {row.codiceSeriale}
            </span>
          )}
          {!row.matricolaInterna && !row.codiceSeriale && "-"}
        </div>
      ),
    },
    {
      key: "ubicazione",
      label: "Ubicazione",
      render: (row) =>
        row.ubicazioneNome ? (
          <Link
            to={`/aree/${row.ubicazioneId}`}
            className="text-gray-600 hover:text-primary-600 text-sm"
          >
            {row.ubicazioneNome}
          </Link>
        ) : (
          <span className="text-gray-400 text-sm">Non specificata</span>
        ),
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => (
        <Link
          to={`/rsa/${row.rsaId}`}
          className="text-gray-600 hover:text-primary-600 text-sm"
        >
          {row.rsaNome}
        </Link>
      ),
    },
    {
      key: "manutenzione",
      label: "Prossima Manutenzione",
      render: (row) => {
        if (!row.dataProssimaManutenzione) {
          return <span className="text-gray-400 text-sm">-</span>;
        }
        const data = new Date(row.dataProssimaManutenzione);
        const oggi = new Date();
        const diffDays = Math.ceil((data - oggi) / (1000 * 60 * 60 * 24));

        let colorClass = "text-gray-600";
        if (diffDays < 0) colorClass = "text-red-600 font-semibold";
        else if (diffDays <= 30) colorClass = "text-orange-600";

        return (
          <span className={`text-sm ${colorClass}`}>
            {data.toLocaleDateString("it-IT")}
            {diffDays < 0 && <span className="ml-1">⚠️</span>}
          </span>
        );
      },
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const statoInfo = STATO_OPTIONS[row.stato] || {
          label: "Sconosciuto",
          variant: "default",
        };
        return (
          <Badge variant={statoInfo.variant}>
            {statoInfo.icon} {statoInfo.label}
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Apparecchiature</h1>
          <p className="text-gray-500 mt-1">
            Gestione apparecchiature tecnologiche delle RSA
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? "Esportazione..." : "Esporta Excel"}
          </Button>
          {isAdmin && (
            <Button
              variant="secondary"
              onClick={() => navigate("/apparecchiature/import")}
            >
              Importa da file
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => navigate("/apparecchiature/nuovo")}>
              + Nuova Apparecchiatura
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.totale}</p>
            <p className="text-sm text-gray-500">Totale</p>
          </Card>
          <Card className="p-4 text-center bg-green-50">
            <p className="text-3xl font-bold text-green-700">
              {stats.perStato?.funzionanti || 0}
            </p>
            <p className="text-sm text-green-600">Funzionanti</p>
          </Card>
          <Card className="p-4 text-center bg-red-50">
            <p className="text-3xl font-bold text-red-700">
              {stats.perStato?.guaste || 0}
            </p>
            <p className="text-sm text-red-600">Guaste</p>
          </Card>
          <Card className="p-4 text-center bg-orange-50">
            <p className="text-3xl font-bold text-orange-700">
              {stats.manutenzioniInScadenza || 0}
            </p>
            <p className="text-sm text-orange-600">Manutenzioni &lt;30gg</p>
          </Card>
          <Card className="p-4 text-center bg-red-50">
            <p className="text-3xl font-bold text-red-700">
              {stats.manutenzioniScadute || 0}
            </p>
            <p className="text-sm text-red-600">Manutenzioni Scadute</p>
          </Card>
        </div>
      )}

      {/* Filtri */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RSA
            </label>
            <select
              value={rsaIdFilter}
              onChange={(e) => handleFilterChange("rsaId", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((rsa) => (
                <option key={rsa.id} value={rsa.id}>
                  {rsa.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={tipoFilter}
              onChange={(e) => handleFilterChange("tipo", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Tutti i tipi</option>
              {Object.entries(TIPO_APPARECCHIATURA_OPTIONS).map(
                ([value, label]) => (
                  <option key={value} value={value}>
                    {TIPO_ICONS[value]} {label}
                  </option>
                ),
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stato
            </label>
            <select
              value={statoFilter}
              onChange={(e) => handleFilterChange("stato", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Tutti gli stati</option>
              {Object.entries(STATO_OPTIONS).map(([value, info]) => (
                <option key={value} value={value}>
                  {info.icon} {info.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prossima manut. dal
            </label>
            <input
              type="date"
              value={scadenzaDaFilter}
              onChange={(e) => handleFilterChange("scadenzaDa", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prossima manut. al
            </label>
            <input
              type="date"
              value={scadenzaAFilter}
              onChange={(e) => handleFilterChange("scadenzaA", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </Card>

      {/* Tabella */}
      <Card>
        <DataTable
          data={apparecchiature}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca per nome, marca, codice..."
          emptyMessage="Nessuna apparecchiatura trovata"
          onRowClick={(row) => navigate(`/apparecchiature/${row.id}`)}
          serverSide
          totalCount={totalCount}
          currentPage={page}
          onPageChange={setPage}
          onSearch={handleSearch}
          onPageSizeChange={handlePageSizeChange}
          pageSize={pageSize}
        />
      </Card>
    </div>
  );
}

export default ApparecchiatureListPage;
